import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Arealplan, ArealplanDomains, CodelistField } from '@origo/planregister';
import { domainLabel } from '@origo/planregister';
import { Combobox, DateInput, Button, FilterPanel } from '@origo/origo-ui';
import { getFilterOptions, countActiveFilters, monthsAgo, type FilterField } from './filterUtils';
import { type PlanFilters, type SortOption, emptyFilters, sortOptions } from './types';
import styles from './PlanFilterPanel.module.css';

export interface PlanFilterPanelProps {
    plans: readonly Arealplan[];
    domains: ArealplanDomains | undefined;
    filters: PlanFilters;
    onFiltersChange: (f: PlanFilters) => void;
    sortOption: SortOption;
    onSortChange: (o: SortOption) => void;
    /** Vis sortering inne i panelet. Sett false når sortering ligger i verktøylinja. Default true. */
    showSortControl?: boolean;
}

interface ComboOption {
    value: string;
    label: string;
}

function formatDateValue(date: Date | null): string {
    return date ? date.toISOString().split('T')[0] : '';
}

const PlanFilterPanel: React.FC<PlanFilterPanelProps> = ({
    plans,
    domains,
    filters,
    onFiltersChange,
    sortOption,
    onSortChange,
    showSortControl = true,
}) => {
    const { t } = useTranslation();

    const update = (patch: Partial<PlanFilters>) => onFiltersChange({ ...filters, ...patch });

    const buildOptions = (field: FilterField, domainField: CodelistField | null): ComboOption[] =>
        getFilterOptions(plans, field).map(o => ({
            value: o.value,
            label: domainField
                ? `${domainLabel(domains, domainField, o.value)} (${o.count})`
                : `${o.value} (${o.count})`,
        }));

    const statusOptions = useMemo(() => buildOptions('planstatus', 'planstatus'), [plans, domains]);
    const plantypeOptions = useMemo(() => buildOptions('plantype', 'plantype'), [plans, domains]);
    const lovreferanseOptions = useMemo(() => buildOptions('lovreferanse', 'lovreferanse'), [plans, domains]);
    const kommuneOptions = useMemo(() => buildOptions('admomrkode', null), [plans]);

    const sortValues: ComboOption[] = sortOptions.map(o => ({
        value: `${o.field}-${o.direction}`,
        label: t(o.labelKey),
    }));

    const isPresetActive = (months: number) =>
        filters.dateFrom != null &&
        Math.abs(filters.dateFrom.getTime() - monthsAgo(months).getTime()) < 86_400_000;

    const setDatePreset = (months: number | null) =>
        months === null
            ? update({ dateFrom: null, dateTo: null })
            : update({ dateFrom: monthsAgo(months), dateTo: new Date() });

    return (
        <FilterPanel
            activeCount={countActiveFilters(filters)}
            onReset={() => onFiltersChange(emptyFilters)}
            resetLabel={t('landing.filter.reset')}
        >
            <Combobox
                label={t('landing.filter.status')}
                multiple
                placeholder={t('landing.filter.statusPlaceholder')}
                options={statusOptions}
                value={filters.statuses}
                onChange={selected => update({ statuses: selected })}
            />
            <Combobox
                label={t('landing.filter.plantype')}
                multiple
                placeholder={t('landing.filter.plantypePlaceholder')}
                options={plantypeOptions}
                value={filters.plantyper}
                onChange={selected => update({ plantyper: selected })}
            />
            <Combobox
                label={t('landing.filter.lovreferanse')}
                multiple
                placeholder={t('landing.filter.lovreferansePlaceholder')}
                options={lovreferanseOptions}
                value={filters.lovreferanser}
                onChange={selected => update({ lovreferanser: selected })}
            />
            <Combobox
                label={t('landing.filter.kommune')}
                multiple
                placeholder={t('landing.filter.kommunePlaceholder')}
                options={kommuneOptions}
                value={filters.kommuner}
                onChange={selected => update({ kommuner: selected })}
            />

            <div className={styles.dateSection}>
                <p className={styles.dateSectionLabel}>{t('landing.filter.dateLabel')}</p>
                <div className={styles.dateRow}>
                    <DateInput
                        label={t('landing.filter.dateFrom')}
                        value={formatDateValue(filters.dateFrom)}
                        onChange={e => update({ dateFrom: e.target.value ? new Date(e.target.value) : null })}
                    />
                    <span className={styles.dateSeparator} aria-hidden>–</span>
                    <DateInput
                        label={t('landing.filter.dateTo')}
                        value={formatDateValue(filters.dateTo)}
                        onChange={e => update({ dateTo: e.target.value ? new Date(e.target.value) : null })}
                    />
                </div>
                <div className={styles.datePresets}>
                    <Button
                        variant={isPresetActive(6) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(6) ? null : 6)}
                    >
                        {t('landing.filter.preset6m')}
                    </Button>
                    <Button
                        variant={isPresetActive(12) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(12) ? null : 12)}
                    >
                        {t('landing.filter.preset12m')}
                    </Button>
                    <Button
                        variant={isPresetActive(60) ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDatePreset(isPresetActive(60) ? null : 60)}
                    >
                        {t('landing.filter.preset60m')}
                    </Button>
                </div>
            </div>

            {showSortControl && (
                <Combobox
                    label={t('landing.filter.sort')}
                    placeholder={t('landing.filter.sort')}
                    options={sortValues}
                    value={[`${sortOption.field}-${sortOption.direction}`]}
                    onChange={selected => {
                        const found = sortOptions.find(o => `${o.field}-${o.direction}` === selected[0]);
                        if (found) onSortChange(found);
                    }}
                />
            )}
        </FilterPanel>
    );
};

export default PlanFilterPanel;
